export const REFRESH_TOKEN_EXPIRY_KEY = "refreshTokenExpiryTime";

export const REFRESH_BUFFER_MS = 60 * 1000;
export const DEFAULT_POLL_MS = 8 * 60 * 1000;
export const MIN_REFRESH_GAP_MS = 15 * 1000;
export const MAX_SET_TIMEOUT_MS = 2147483647;

let sessionRevision = 0;
let lastRefreshAttemptAt = null;
let retryNotBeforeAt = null;

export function bumpSessionRevision() {
  sessionRevision += 1;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("session-revision-bump"));
  }
}

export function getSessionRevision() {
  return sessionRevision;
}

export function recordRefreshAttempt() {
  lastRefreshAttemptAt = Date.now();
}

export function getLastRefreshAttemptAt() {
  return lastRefreshAttemptAt;
}

export function clearRetryHold() {
  retryNotBeforeAt = null;
}

export function setRetryHoldEightMinutes() {
  retryNotBeforeAt = Date.now() + DEFAULT_POLL_MS;
}

export function persistRefreshTokenExpiryTime(expiryTime) {
  if (expiryTime == null || expiryTime === "") {
    return;
  }
  sessionStorage.setItem(REFRESH_TOKEN_EXPIRY_KEY, String(expiryTime));
}

export function parseRefreshTokenExpiryMs(expiryTime) {
  if (expiryTime == null || expiryTime === "") {
    return null;
  }

  if (typeof expiryTime === "number") {
    if (!Number.isFinite(expiryTime)) {
      return null;
    }
    return expiryTime > 1e12 ? expiryTime : expiryTime * 1000;
  }

  const str = String(expiryTime).trim();
  if (!str) {
    return null;
  }

  if (/^\d+$/.test(str)) {
    if (str.length <= 10) {
      return Number(str) * 1000;
    }
    return Number(str);
  }

  const normalized = str.replace(/([+-]\d{2})(\d{2})$/, "$1:$2");
  const parsed = new Date(normalized).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

export function getAccessTokenExpiryMs(accessToken) {
  if (!accessToken || typeof accessToken !== "string") {
    return null;
  }

  const parts = accessToken.split(".");
  if (parts.length < 2) {
    return null;
  }

  try {
    let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = payload.length % 4;
    if (pad) {
      payload += "=".repeat(4 - pad);
    }

    const binary = atob(payload);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    const exp = JSON.parse(json).exp;

    if (typeof exp !== "number" || !Number.isFinite(exp)) {
      return null;
    }

    return exp * 1000;
  } catch (e) {
    return null;
  }
}

function computeBaseRefreshDelayMs(now, accessToken) {
  const accessExpiryMs = getAccessTokenExpiryMs(accessToken);
  const refreshExpiryMs = parseRefreshTokenExpiryMs(
      sessionStorage.getItem(REFRESH_TOKEN_EXPIRY_KEY)
  );

  if (accessExpiryMs != null) {
    let deadline = accessExpiryMs;
    if (refreshExpiryMs != null) {
      deadline = Math.min(deadline, refreshExpiryMs);
    }
    return Math.max(deadline - now - REFRESH_BUFFER_MS, 0);
  }

  if (refreshExpiryMs != null && refreshExpiryMs - now <= DEFAULT_POLL_MS) {
    return Math.max(refreshExpiryMs - now - REFRESH_BUFFER_MS, 0);
  }

  return DEFAULT_POLL_MS;
}

export function getRefreshDelayMs(accessToken) {
  const now = Date.now();
  let delay = computeBaseRefreshDelayMs(now, accessToken);

  if (retryNotBeforeAt != null) {
    delay = Math.max(delay, retryNotBeforeAt - now);
  }

  if (lastRefreshAttemptAt != null) {
    delay = Math.max(delay, lastRefreshAttemptAt + MIN_REFRESH_GAP_MS - now);
  }

  if (delay > MAX_SET_TIMEOUT_MS) {
    delay = 0;
  }

  return Math.max(delay, 0);
}

export function mergeUserProfile(existing, incoming) {
  if (!incoming) {
    return existing;
  }
  if (!existing) {
    return incoming;
  }
  return {...existing, ...incoming};
}

export default {
  REFRESH_TOKEN_EXPIRY_KEY,
  REFRESH_BUFFER_MS,
  DEFAULT_POLL_MS,
  MIN_REFRESH_GAP_MS,
  bumpSessionRevision,
  getSessionRevision,
  recordRefreshAttempt,
  getLastRefreshAttemptAt,
  clearRetryHold,
  setRetryHoldEightMinutes,
  persistRefreshTokenExpiryTime,
  parseRefreshTokenExpiryMs,
  getAccessTokenExpiryMs,
  getRefreshDelayMs,
  mergeUserProfile,
};
